<template>
  <div class="navbar">
    <Hamburger
      :is-active="sidebar.opened"
      class="hamburger-container"
      @toggleClick="toggleSideBar"
    />

    <Breadcrumb class="breadcrumb-container" />

    <div class="right-menu">
      <el-dropdown class="avatar-container" trigger="click">
        <div class="avatar-wrapper">
          <div class="user-text">
            <b>{{ name }}</b>
            <template v-if="username">({{ username }})</template>
          </div>
          <i class="el-icon-caret-bottom" />
        </div>
        <el-dropdown-menu slot="dropdown" class="user-dropdown">
          <a v-if="siteUrl" target="_blank" :href="siteUrl">
            <el-dropdown-item>Открыть сайт</el-dropdown-item>
          </a>
          <el-dropdown-item @click.native="openChangePasswordModal">
            Сменить пароль
          </el-dropdown-item>
          <el-dropdown-item :divided="divided" @click.native="logout">
            Выйти
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Breadcrumb from "@/components/Breadcrumb";
import Hamburger from "@/components/Hamburger";
import { setRoleToken } from "@/utils/auth";
import Cookies from "js-cookie";

export default {
  components: {
    Breadcrumb,
    Hamburger,
  },
  data() {
    return {
      passwordWarningShown: false,
    };
  },
  computed: {
    divided() {
      return !!this.siteUrl;
    },
    ...mapGetters([
      "sidebar",
      "name",
      "username",
      "siteUrl",
      "daysBeforePasswordExpires",
    ]),
  },
  watch: {
    // Наблюдаем за изменением срока действия пароля
    daysBeforePasswordExpires: {
      handler(newVal) {
        // Проверяем и показываем предупреждение при изменении значения
        if (newVal !== null && !this.passwordWarningShown) {
          this.checkAndShowPasswordWarning();
        }
      },
      immediate: true,
    },
  },
  methods: {
    toggleSideBar() {
      this.$store.dispatch("app/toggleSideBar");
    },
    async logout() {
      await this.$store.dispatch("user/logout");
      this.$router.push(`/login?redirect=${this.$route.fullPath}`);
      setRoleToken("");
    },
    openChangePasswordModal() {
      this.$store.dispatch("password/showChangePasswordModal");
    },
    pluralize(number, words) {
      const cases = [2, 0, 1, 1, 1, 2];
      return words[
        number % 100 > 4 && number % 100 < 20
          ? 2
          : cases[number % 10 < 5 ? number % 10 : 5]
      ];
    },
    // Проверка, закрывалось ли предупреждение сегодня
    hasClosedWarningToday() {
      const cookieName = "passwordWarningClosed";
      const closedDate = Cookies.get(cookieName);
      if (!closedDate) return false;

      const today = new Date().toDateString();
      return closedDate === today;
    },
    // Проверка и показ предупреждения о сроке действия пароля
    checkAndShowPasswordWarning() {
      // Проверяем, нужно ли показывать предупреждение
      if (
        this.daysBeforePasswordExpires === null ||
        this.passwordWarningShown
      ) {
        return;
      }

      // Проверяем, не закрывалось ли предупреждение сегодня
      if (this.hasClosedWarningToday()) {
        return;
      }

      // Показываем предупреждение, если срок действия пароля от 1 до 21 дня
      if (
        this.daysBeforePasswordExpires <= 21 &&
        this.daysBeforePasswordExpires > 0
      ) {
        this.showPasswordWarning();
      }
    },
    // Показ предупреждения о сроке действия пароля
    showPasswordWarning() {
      // Устанавливаем флаг, что предупреждение показано
      this.passwordWarningShown = true;

      // Определяем, является ли предупреждение критическим (5 дней или меньше)
      const isCritical = this.daysBeforePasswordExpires <= 5;

      // Формируем сообщение
      const message = `Срок действия вашего текущего пароля истечет через ${
        this.daysBeforePasswordExpires
      } ${this.pluralize(this.daysBeforePasswordExpires, [
        "день",
        "дня",
        "дней",
      ])}. Рекомендуем заранее придумать и установить новый пароль.`;

      if (isCritical) {
        // Для критических предупреждений (5 дней или меньше) показываем диалог, который нельзя закрыть
        this.$confirm(message, "Внимание", {
          confirmButtonText: "Сменить пароль",
          cancelButtonText: "Выйти",
          type: "warning",
          closeOnClickModal: false,
          closeOnPressEscape: false,
          showClose: false,
        })
          .then(() => {
            // Пользователь нажал "Сменить пароль"
            this.openChangePasswordModal();
          })
          .catch(() => {
            // Пользователь нажал "Выйти"
            this.logout();
          });
      } else {
        // Для обычных предупреждений (6-21 дней) показываем обычное сообщение с возможностью закрытия
        this.$msgbox({
          message: message,
          title: "Уведомление",
          showCancelButton: true,
          confirmButtonText: "Сменить пароль",
          cancelButtonText: "Закрыть",
          type: "warning",
          distinguishCancelAndClose: true,
        })
          .then((action) => {
            if (action === "confirm") {
              // Пользователь нажал "Сменить пароль"
              this.openChangePasswordModal();
            }
          })
          .catch(() => {
            // Пользователь закрыл диалог или нажал "Закрыть"
            // Устанавливаем cookie, чтобы не показывать снова сегодня
            const cookieName = "passwordWarningClosed";
            const today = new Date().toDateString();
            Cookies.set(cookieName, today, { expires: 1 }); // Истекает через 1 день
          });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.navbar {
  height: 50px;
  overflow: hidden;
  position: relative;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);

  .hamburger-container {
    line-height: 46px;
    height: 100%;
    float: left;
    cursor: pointer;
    transition: background 0.3s;
    -webkit-tap-highlight-color: transparent;

    &:hover {
      background: rgba(0, 0, 0, 0.025);
    }
  }

  .breadcrumb-container {
    float: left;
  }

  .right-menu {
    float: right;
    height: 100%;
    line-height: 50px;

    &:focus {
      outline: none;
    }

    .right-menu-item {
      display: inline-block;
      padding: 0 8px;
      height: 100%;
      font-size: 18px;
      color: #5a5e66;
      vertical-align: text-bottom;

      &.hover-effect {
        cursor: pointer;
        transition: background 0.3s;

        &:hover {
          background: rgba(0, 0, 0, 0.025);
        }
      }
    }

    .avatar-container {
      margin-right: 30px;
      cursor: pointer;
      .avatar-wrapper {
        display: flex;
        align-items: center;
        .user-text {
          font-size: 14px;
        }
        .user-avatar {
          cursor: pointer;
          width: 40px;
          height: 40px;
          border-radius: 10px;
        }
        .el-icon-caret-bottom {
          margin-left: 10px;
        }
      }
    }
  }
}
</style>
